import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";

const DEFAULT_ACTIVE_DAYS = 30;
/** The version-filter dropdown's "no filter" sentinel — never sent as a real
 * `?appVersion=` value (an empty select value would be indistinguishable from
 * "not yet chosen"). */
const ALL_VERSIONS = "__all__";
/** Matches `deviceAnalytics.ts`'s `UNKNOWN_APP_VERSION` bucket key
 * (`GET /admin/devices/stats`'s `byVersion`) — a device that never sent
 * `X-Client-Version` has `appVersion: null`, shown/filtered under this label. */
const UNKNOWN_VERSION = "unknown";

/**
 * Devices tab, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v2) — mirrors
 * `apps/admin-web/src/pages/DevicesPage.tsx` (itself mirroring `apps/admin/app/(tabs)/devices.tsx` /
 * `specs/admin/screens/devices.md`): the aggregate over the `devices` table (docs/300) PLUS (v1) a
 * per-device table — every connected device, its coarse type, and its app version — with a
 * client-side filter by version (docs/300 v1, "surface each connected device's app version ... and
 * let the admin filter the devices list by app version"). The `stats` query is lifted unchanged from
 * v0; the per-device list is new here.
 */
export function DevicesPage() {
  const { client, token } = useAuth();
  const [activeDaysInput, setActiveDaysInput] = useState(String(DEFAULT_ACTIVE_DAYS));
  const [activeDays, setActiveDays] = useState<number | undefined>(DEFAULT_ACTIVE_DAYS);
  const [versionFilter, setVersionFilter] = useState(ALL_VERSIONS);

  const statsQuery = useQuery({
    queryKey: ["admin-device-stats", activeDays],
    queryFn: () => client!.adminDeviceStats(token!, { activeDays }),
    enabled: !!client && !!token,
  });
  const stats = statsQuery.data;

  // The full, unfiltered device list, fetched once — the version dropdown's
  // options AND the table's rows both derive from it, filtered client-side
  // (docs/300 v1 explicitly allows a client-side filter; it also sidesteps the
  // server-side `?appVersion=` filter's one limitation — it can't express
  // "devices with no version at all," since a device that never sent
  // X-Client-Version has `appVersion: null`, not the literal string "unknown").
  const devicesQuery = useQuery({
    queryKey: ["admin-devices"],
    queryFn: () => client!.adminDevices(token!),
    enabled: !!client && !!token,
  });
  const allDevices = devicesQuery.data ?? [];

  const refresh = () => {
    const parsed = Number(activeDaysInput);
    setActiveDays(Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : undefined);
  };

  const typeEntries = Object.entries(stats?.byType ?? {}).sort((a, b) => b[1] - a[1]);
  const versionEntries = Object.entries(stats?.byVersion ?? {}).sort((a, b) => b[1] - a[1]);
  const perAccount = stats?.perAccount ?? [];

  const versionOptions = useMemo(() => {
    const set = new Set(allDevices.map((d) => d.appVersion ?? UNKNOWN_VERSION));
    return [...set].sort();
  }, [allDevices]);

  const filteredDevices = useMemo(() => {
    if (versionFilter === ALL_VERSIONS) return allDevices;
    return allDevices.filter((d) => (d.appVersion ?? UNKNOWN_VERSION) === versionFilter);
  }, [allDevices, versionFilter]);

  return (
    <>
      <MainCard title="Devices" sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
          <TextField
            type="number"
            size="small"
            label="Active in the last N days"
            sx={{ maxWidth: 200 }}
            value={activeDaysInput}
            onChange={(e) => setActiveDaysInput(e.target.value)}
          />
          <Button variant="outlined" color="secondary" onClick={refresh}>
            Refresh
          </Button>
        </Box>
      </MainCard>

      {statsQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(statsQuery.error as Error).message}
        </Alert>
      ) : null}
      {statsQuery.isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !stats || stats.total === 0 ? (
        <Typography color="text.secondary">No devices recorded yet.</Typography>
      ) : (
        <>
          <MainCard sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Total distinct devices
            </Typography>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
              Active in the last {stats.activeDays} day(s): {stats.activeLastNDays}
            </Typography>
          </MainCard>

          <MainCard title="By type" sx={{ mb: 2 }}>
            <Stack spacing={1}>
              {typeEntries.map(([deviceType, count]) => (
                <Box key={deviceType} sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>{deviceType}</Typography>
                  <Typography color="text.secondary">{count}</Typography>
                </Box>
              ))}
            </Stack>
          </MainCard>

          <MainCard title="By version" sx={{ mb: 2 }}>
            <Stack spacing={1}>
              {versionEntries.map(([version, count]) => (
                <Box key={version} sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontFamily: version === UNKNOWN_VERSION ? undefined : "monospace" }}>
                    {version}
                  </Typography>
                  <Typography color="text.secondary">{count}</Typography>
                </Box>
              ))}
            </Stack>
          </MainCard>

          <MainCard title="Per account">
            <Stack spacing={1}>
              {perAccount.map((row) => (
                <Box key={row.userId} sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {row.userId}
                  </Typography>
                  <Typography color="text.secondary">{row.count}</Typography>
                </Box>
              ))}
            </Stack>
          </MainCard>
        </>
      )}

      <MainCard
        title="All devices"
        sx={{ mt: 2 }}
        secondary={
          <TextField
            select
            size="small"
            label="Version"
            sx={{ minWidth: 160 }}
            value={versionFilter}
            onChange={(e) => setVersionFilter(e.target.value)}
          >
            <MenuItem value={ALL_VERSIONS}>All versions</MenuItem>
            {versionOptions.map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </TextField>
        }
      >
        {devicesQuery.isError ? (
          <Alert severity="error">{(devicesQuery.error as Error).message}</Alert>
        ) : devicesQuery.isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredDevices.length === 0 ? (
          <Typography color="text.secondary">
            {allDevices.length === 0 ? "No devices recorded yet." : "No devices match this filter."}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>First seen</TableCell>
                  <TableCell>Last seen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDevices.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{d.userId}</TableCell>
                    <TableCell>{d.deviceType}</TableCell>
                    <TableCell>{d.platform ?? "—"}</TableCell>
                    <TableCell>{d.appVersion ?? "—"}</TableCell>
                    <TableCell>{new Date(d.firstSeenAt * 1000).toLocaleString()}</TableCell>
                    <TableCell>{new Date(d.lastSeenAt * 1000).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MainCard>
    </>
  );
}
